import { useState, useEffect, useMemo, useRef } from 'react';
import { City, Collection, Image } from 'src/types';
import ImageWithFallback from 'src/components/ImageWithFallback';
import classNames from 'classnames';
import { AppRoutes } from 'src/lib/routes';
import Link from 'next/link';
import { truncate } from 'lodash-es';
import { commonCarouselSettings } from 'src/lib/constants';
import ClientOnlyComponent from '../ClientOnlyComponent';
import SliderOrDiv from '../SliderOrDiv';
import useBreakpoints from 'src/hooks/useBreakpoints';
import Slider from 'react-slick';

type Record = {
  id: number | string;
  slug: string;
  image?: Image;
  strippedDescription?: string;
  description?: string;
  name: string;
  slugCode?: string;
  location?: { name: string };
  tag?: { name: string };
};

type Tab = 'todo' | 'area' | 'list';

type Props = {
  record: {
    type: 'City' | 'Country';
    name: string;
    slug: string;
  };
  activityCollections: Collection[];
  listCollections: Collection[];
  locationCollections?: Collection[];
  cities?: City[];
  defaultTab?: Tab;
  title?: string;
  limit?: number;
  onShowMore?: (tab: Tab) => void;
};

const TopCollectionSection: React.FC<Props> = ({
  record,
  activityCollections,
  locationCollections,
  listCollections,
  cities,
  defaultTab,
  title,
  limit,
  onShowMore,
}) => {
  const safeTab =
    activityCollections.length > 0 ? 'todo' : listCollections.length > 0 ? 'list' : 'area';
  const initialTab = defaultTab ? (defaultTab !== 'area' ? safeTab : 'area') : safeTab;
  const [tab, setTab] = useState<Tab>(initialTab);
  const location =
    (locationCollections && locationCollections[0]) || ((cities && cities[0]) as Record);
  const initialCollection =
    initialTab === 'todo'
      ? activityCollections[0]
      : initialTab === 'list'
      ? listCollections[0]
      : location;

  const [currentCollection, setCurrentCollection] = useState<Record>(initialCollection);
  const { isScreenMd } = useBreakpoints();
  const sliderRef = useRef<Slider>(null);

  useEffect(() => {
    setCurrentCollection(initialCollection);
    setTab(initialTab);
  }, [record.type, record.slug, initialTab]);

  useEffect(() => {
    setCurrentCollection(
      tab === 'todo' ? activityCollections[0] : tab === 'list' ? listCollections[0] : location
    );
  }, [tab, location?.id]);

  const records =
    tab === 'todo'
      ? activityCollections
      : tab === 'list'
      ? listCollections
      : cities || locationCollections || [];

  return currentCollection ? (
    <div className="tw-py-16 md:tw-py-24 tw-bg-brand-navy">
      <div className="container">
        {title ? (
          <div className="tw-text-center tw-text-brand-green tw-mb-12">
            <div className="tw-text-sm tw-font-semibold tw-mb-2">EXPLORE BY</div>
            <div className="tw-text-5xl tw-font-semibold tw-font-styleSans tw-leading-none md:tw-leading-none">
              {title}
            </div>
          </div>
        ) : null}
        <div className="row tw-items-start">
          <div className="col-md-4">
            {[listCollections, activityCollections, cities || locationCollections || []].filter(
              (o) => o.length > 0
            ).length > 1 ? (
              <div className="tw-border tw-border-white tw-rounded-4xl tw-p-1 tw-flex tw-items-center tw-justify-between tw-mx-5 md:tw-mx-0 tw-mb-8">
                <div
                  className={classNames(
                    'btn btn-primary tw-flex-1 hover:tw-bg-brand-green tw-px-4 tw-py-3 md:tw-text-base tw-text-sm tw-font-styleSans tw-mr-2',
                    {
                      'tw-bg-transparent tw-border-0': tab !== 'todo',
                      'tw-bg-brand-green tw-text-brand-navy': tab === 'todo',
                      'tw-hidden': activityCollections.length === 0,
                    }
                  )}
                  onClick={() => {
                    setTab('todo');
                  }}
                >
                  activities
                </div>
                <div
                  className={classNames(
                    'btn btn-primary tw-flex-1 hover:tw-bg-brand-green tw-px-4 tw-py-3 md:tw-text-base tw-text-sm tw-font-styleSans',
                    {
                      'tw-bg-transparent tw-border-0': tab !== 'area',
                      'tw-bg-brand-green tw-text-brand-navy': tab === 'area',
                      'tw-mr-2': listCollections.length > 0,
                      'tw-hidden': (cities || locationCollections || []).length === 0,
                    }
                  )}
                  onClick={() => {
                    setTab('area');
                  }}
                >
                  {record.type === 'Country' ? 'cities' : 'areas'}
                </div>
                <div
                  className={classNames(
                    'btn btn-primary tw-flex-1 hover:tw-bg-brand-green tw-px-4 tw-py-3 md:tw-text-base tw-text-sm tw-font-styleSans',
                    {
                      'tw-bg-transparent tw-border-0': tab !== 'list',
                      'tw-bg-brand-green tw-text-brand-navy': tab === 'list',
                      'tw-hidden': listCollections.length === 0,
                    }
                  )}
                  onClick={() => {
                    setTab('list');
                  }}
                >
                  our picks
                </div>
              </div>
            ) : null}

            {records ? (
              <>
                <ClientOnlyComponent>
                  <SliderOrDiv
                    key={tab}
                    {...commonCarouselSettings}
                    isSlider={!isScreenMd}
                    sliderRef={sliderRef}
                    className={
                      isScreenMd
                        ? 'tw-overflow-y-auto custom-scrollbar tw-h-96'
                        : 'carousel-top-collection tw--mx-2'
                    }
                    dots={records.length > 4}
                    arrows={false}
                    fade={true}
                    rows={2}
                    slidesPerRow={2}
                  >
                    {(records as Record[]).slice(0, limit ? limit : records.length).map((item) => (
                      <div
                        key={item.id}
                        className="md:tw-mb-1 md:tw-px-0 col-md-12 col-6 tw-px-2 tw-mb-8 tw-align-top"
                      >
                        <Link
                          legacyBehavior prefetch={false}
                          href={
                            tab === 'list' ||
                            tab === 'todo' ||
                            (tab === 'area' && record.type === 'City')
                              ? AppRoutes.collectionRoute({
                                  id: item.slugCode || item.slug,
                                }).toPath()
                              : AppRoutes.cityRoute({
                                  id: item.slug,
                                }).toPath()
                          }
                        >
                          <a
                            className={classNames(
                              'md:tw-text-white tw-text-brand-green md:tw-mb-2.5 tw-block md:tw-text-4xl hover:md:tw-text-brand-green md:tw-text-left tw-text-sm tw-text-center tw-text-xl tw-font-styleSans tw-font-semibold tw-underline tw-leading-none md:tw-leading-none',
                              {
                                'md:tw-text-brand-green md:tw-underline':
                                  currentCollection.id === item.id,
                                'md:tw-no-underline': currentCollection.id !== item.id,
                              }
                            )}
                            onMouseEnter={() => {
                              setCurrentCollection(item);
                            }}
                          >
                            <div className="tw-mb-4 tw-outline-none md:tw-hidden">
                              <ImageWithFallback
                                record={item.image}
                                imgField="webpMdUrl"
                                alt=""
                                className="tw-static tw-object-cover tw-h-32 tw-w-full tw-border-2 tw-border-brand-green tw-border-solid tw-rounded-lg"
                                fill
                              />
                            </div>
                            {tab === 'list'
                              ? item.name
                              : tab === 'todo'
                              ? item.tag?.name
                              : record.type === 'City'
                              ? item.location?.name
                              : item.name}
                          </a>
                        </Link>
                      </div>
                    ))}
                    {limit && limit < records.length ? (
                      <div
                        className="tw-leading-none md:tw-leading-none tw-hidden md:tw-block tw-underline md:tw-text-left tw-text-white tw-font-semibold tw-cursor-pointer tw-mt-6 tw-font-styleSans tw-text-sm md:tw-text-base"
                        onClick={() => {
                          if (typeof onShowMore == 'function') {
                            onShowMore(tab);
                          }
                        }}
                      >
                        show more
                      </div>
                    ) : null}
                  </SliderOrDiv>
                </ClientOnlyComponent>
              </>
            ) : null}
          </div>
          <div className="tw-hidden md:tw-block col-8 tw-pl-24 md:tw-pt-10">
            <div className="tw-relative">
              <div className="tw-relative" style={{ height: '450px' }}>
                <Link
                  legacyBehavior prefetch={false}
                  href={(cities
                    ? tab === 'area'
                      ? AppRoutes.cityRoute({ id: currentCollection.slug })
                      : AppRoutes.collectionRoute({ id: currentCollection.slug })
                    : AppRoutes.collectionRoute({ id: currentCollection.slug })
                  ).toPath()}
                >
                  <a>
                    <ImageWithFallback
                      record={currentCollection.image}
                      imgField="webpLgUrl"
                      alt=""
                      className="tw-w-full tw-h-full tw-object-cover tw-border-2 tw-border-solid tw-rounded-lg tw-border-brand-green"
                      fill
                    />
                  </a>
                </Link>
              </div>

              {currentCollection.strippedDescription || currentCollection.description ? (
                <div
                  className="tw-bg-brand-green tw-rounded-2xl tw-p-8 tw-absolute tw--left-8 tw--top-10 tw-max-w-md"
                  dangerouslySetInnerHTML={{
                    __html: truncate(
                      currentCollection.strippedDescription || currentCollection.description,
                      { length: 100, omission: '...' }
                    ),
                  }}
                ></div>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </div>
  ) : null;
};

export default TopCollectionSection;
